import { identifyUser as identifyLogRocketUser } from '@shared/plugins/logRocket';
import { useUserStore } from '@shared/stores/user';
import { useLDClient } from 'launchdarkly-vue-client-sdk';
import { storeToRefs } from 'pinia';
import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw
} from 'vue-router';
import { getCurrentUser } from 'vuefire';

import routes from './routes';
import sharedRoutes from './sharedRoutes';

let allRoutes = [] as RouteRecordRaw[];
allRoutes = allRoutes.concat(routes, sharedRoutes);

const router = createRouter({
  history: createWebHistory(),
  routes: allRoutes
});

router.beforeEach(async (to) => {
  const userStore = useUserStore();
  const { currentS3User } = storeToRefs(userStore);
  const ldClient = useLDClient();
  // routes with `meta: { requiresAuth: true }` will check for the users, others won't
  if (to.meta.requiresAuth) {
    const currentFirebaseUser = await getCurrentUser();
    // if the user is not logged in, redirect to the login page
    if (!currentFirebaseUser) {
      return {
        path: '/login',
        query: {
          // we keep the current path in the query so we can
          // redirect to it after login with
          // `router.push(route.query.redirect || '/')`
          redirect: to.fullPath
        }
      };
    }
    userStore.setCurrentAuthenticatedUser(currentFirebaseUser);
    if (currentS3User.value?.email !== currentFirebaseUser.email) {
      await userStore.fetchCurrentS3User();
    }
    if (currentFirebaseUser?.email) {
      identifyLogRocketUser(currentFirebaseUser.email);
      await ldClient.identify({
        kind: 'user',
        key: currentFirebaseUser.uid,
        name: currentFirebaseUser.displayName || 'unknown',
        email: currentFirebaseUser.email
      });
    }
  }
});

router.afterEach((to, from, failure) => {
  if (failure) return false;

  if (to.meta.title)
    window.document.title = `${to.meta.title || to.name} | Sealed`;
});

router.onError((error) => {
  // On deploy if a dynamic import changes the hash will change causing the
  // dynmaic import to fail. This will reload the page and the new hash will
  // be correct.
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    window.location.reload();
  }
});

export default router;
